import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Check, FolderOpen, Pencil, X } from "lucide-react";

export default function ProjectsTab({ snapshot }) {
  const projects = snapshot.projects || [];
  const activeId = snapshot.active_project_id;
  const [newName, setNewName] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const createProject = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/projects", { name: newName.trim(), copy_from: copyFrom || null });
      setNewName("");
      setCopyFrom("");
      toast.success("Projekt angelegt");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (p) => {
    if (p.id === activeId) return;
    try {
      await api.post(`/projects/${p.id}/activate`);
      toast.success(`Projekt geöffnet: ${p.name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveRename = async (p) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/projects/${p.id}`, { name: editName.trim() });
      setEditId(null);
      toast.success("Umbenannt");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Projekt "${p.name}" mit allen Daten unwiderruflich löschen?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Projekt gelöscht");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Projekte / Veranstaltungen</h2>
        <p className="text-sm text-[#8A99AD] mb-4">
          Jede Veranstaltung hat eigene Teams, Spiele, Tabelle, Ticker, Werbung, Spielfelder und Einstellungen. Es kann immer nur ein Projekt geöffnet sein – Admin und alle Displays folgen dem geöffneten Projekt.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createProject()}
            placeholder="Name der neuen Veranstaltung"
            data-testid="new-project-input"
            className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm flex-1 min-w-[220px]"
          />
          <select
            value={copyFrom}
            onChange={(e) => setCopyFrom(e.target.value)}
            data-testid="copy-from-select"
            className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm cursor-pointer"
            title="Teams, Gruppen und Spielfelder aus einem Projekt übernehmen"
          >
            <option value="">Leeres Projekt</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>Vorlage: {p.name}</option>
            ))}
          </select>
          <button onClick={createProject} disabled={busy} data-testid="create-project-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            <Plus className="w-4 h-4" /> Neues Projekt
          </button>
        </div>
        {copyFrom && <p className="text-xs text-[#00F0FF] mt-2">Teams, Gruppen &amp; Spielfelder werden übernommen (keine Spiele/Tabelle/Werbung).</p>}
      </section>

      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <div className="space-y-2">
          {projects.map((p) => {
            const isActive = p.id === activeId;
            return (
              <div key={p.id} data-testid={`project-row-${p.id}`}
                className={`flex flex-wrap items-center gap-2 rounded-xl border px-4 py-3 transition-colors ${
                  isActive ? "border-[#E5FF00] bg-[#0A0C10] shadow-[0_0_18px_rgba(229,255,0,0.18)]" : "border-[#1E2638] bg-[#0A0C10]"
                }`}>
                <div className={`w-2.5 h-2.5 rounded-full ${isActive ? "bg-[#E5FF00]" : "bg-[#1E2638]"}`} />
                {editId === p.id ? (
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename(p)}
                    data-testid={`project-rename-input-${p.id}`} autoFocus
                    className="bg-[#121620] border border-[#1E2638] rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#00F0FF] flex-1 min-w-[180px]" />
                ) : (
                  <span className="font-display font-bold text-base flex-1 min-w-[160px]">{p.name}</span>
                )}
                {isActive && (
                  <span className="text-[10px] uppercase tracking-widest text-[#E5FF00] border border-[#E5FF00]/40 rounded px-2 py-0.5">Geöffnet</span>
                )}

                {editId === p.id ? (
                  <>
                    <button onClick={() => saveRename(p)} data-testid={`project-rename-save-${p.id}`} className="p-2 rounded-lg bg-[#00F0FF] text-black hover:bg-white transition-colors"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditId(null)} className="p-2 rounded-lg bg-[#121620] border border-[#1E2638] text-[#8A99AD] hover:text-white"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    {!isActive && (
                      <button onClick={() => activate(p)} data-testid={`project-open-${p.id}`} className="flex items-center gap-1.5 bg-[#00F0FF] text-black font-display font-bold uppercase tracking-wider text-xs px-3 py-2 rounded-lg hover:bg-white transition-colors">
                        <FolderOpen className="w-4 h-4" /> Öffnen
                      </button>
                    )}
                    <button onClick={() => { setEditId(p.id); setEditName(p.name); }} data-testid={`project-edit-${p.id}`} className="p-2 rounded-lg bg-[#121620] border border-[#1E2638] text-[#8A99AD] hover:text-[#00F0FF]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} data-testid={`project-delete-${p.id}`} className="p-2 rounded-lg bg-[#121620] border border-[#1E2638] text-[#8A99AD] hover:text-[#FF2A55]"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            );
          })}
          {projects.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Projekte.</p>}
        </div>
      </section>
    </div>
  );
}
